import { useCallback, useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { API, EmptyBox, ErrorBox, LoadingBox, TabHeader, asList, fmtCell, fmtDate, labelFor } from './AdminShared';

const MAIN_KEYS = ['action', 'admin', 'admin_email', 'created_at', 'timestamp'];
const SKIP_KEYS = new Set(['id', 'created_at', 'timestamp', 'details']);

export const AdminAudit = ({ authHeaders, tx }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API}/api/admin/app-audit?limit=50`, { headers: authHeaders() });
      if (!res.ok) throw new Error('audit');
      setItems(asList(await res.json(), ['entries', 'items', 'logs', 'audit']));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="app-audit-section">
      <TabHeader icon={History} title={tx.audit.title} count={items.length} onRefresh={load} refreshLabel={tx.common.refresh} testid="app-audit" />

      <div className="mt-6 space-y-3">
        {loading && <LoadingBox text={tx.common.loading} testid="app-audit-loading" />}
        {!loading && error && (
          <ErrorBox text={tx.common.unavailable} retryText={tx.common.retry} onRetry={load} testid="app-audit-error" />
        )}
        {!loading && !error && items.length === 0 && <EmptyBox text={tx.audit.empty} testid="app-audit-empty" />}
        {!loading &&
          !error &&
          items.map((entry, i) => {
            const who = entry.admin || entry.admin_email || entry.source || '';
            const when = entry.created_at || entry.timestamp;
            const extra = Object.entries(entry).filter(
              ([k]) => !SKIP_KEYS.has(k) && !MAIN_KEYS.includes(k),
            );
            return (
              <article key={entry.id || i} data-testid={`audit-entry-${entry.id || i}`} className="rounded-2xl glass-dark px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-white font-bold">
                    {labelFor(entry.action || 'action', tx.fields)}
                    {who && <span className="ml-2 text-xs font-medium text-krug-tealbright">{who}</span>}
                  </p>
                  <span className="text-xs text-slate-500">{fmtDate(when)}</span>
                </div>
                {extra.length > 0 && (
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                    {extra.map(([k, v]) => `${labelFor(k, tx.fields)}: ${fmtCell(k, v)}`).join(' · ')}
                  </p>
                )}
                {entry.details && typeof entry.details === 'object' && (
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                    {Object.entries(entry.details)
                      .map(([k, v]) => `${labelFor(k, tx.fields)}: ${fmtCell(k, v)}`)
                      .join(' · ')}
                  </p>
                )}
              </article>
            );
          })}
      </div>
    </section>
  );
};

export default AdminAudit;
